import { Box, BoxProps, Tooltip } from '@material-ui/core';
import {
  CheckoutSessionStatus,
  CreditCardStatus,
  LoanCriteria,
  LoanStatus,
  LoanStatusExternal,
} from '@nemuru-eng/js-api-client';

import { CREDIT_CARD_STATUS_DEFINITIONS } from '@/components/modules/CreditCard/List/constants/creditCardStatusConfig';
import { isCoborrowerUnderManagement } from '@/components/modules/LoanListByProduct/utils/LoanDataGridFields/loanHasCoborrowers';
import { Icon } from '@/components/shared/components/Icon';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useUser } from '@/hooks/useUser';
import { adjustColorToPalette } from '@/utils/getColorPalette';

import { ORDER_STATUS_DEFINITIONS } from '../../../config/orderConfig';
import { ApplicationType } from '../../../config/types';
import { getLoanStatusListDefinitions } from '../../../utils/getLoanStatusListDefinitions';
import { ErrorAndIncidenceDescription } from './ErrorAndIncidenceDescription';
import { ErrorIcon } from './ErrorIcon/ErrorIcon';
import { Font } from './Font';
import { Text } from './Text';

export interface DetailedLoanStatusTagProps {
  status: LoanStatus | LoanStatusExternal | CheckoutSessionStatus | CreditCardStatus;
  applicationType: ApplicationType;
  loanCriteria?: LoanCriteria;
  boxProps?: BoxProps;
  hideSubText?: boolean;
  loan?: LoanCriteria;
}

export const DetailedLoanStatusTag = ({
  status,
  applicationType,
  loanCriteria,
  boxProps,
  hideSubText,
  loan,
}: DetailedLoanStatusTagProps) => {
  if (!status) return null;
  const { palette } = useCustomTheme();
  const { getTextIdTranslation } = useTranslation();
  const { userEntity } = useUser();

  const richStatus =
    applicationType === ApplicationType.ORDER || applicationType === ApplicationType.CHECKOUT_SESSION
      ? ORDER_STATUS_DEFINITIONS[status]
      : applicationType === ApplicationType.CARD
      ? CREDIT_CARD_STATUS_DEFINITIONS[status]
      : getLoanStatusListDefinitions(loanCriteria?.loanApplicationSelected?.loanLenderBorrowerContract)[status];

  const errorDetected = loanCriteria?.errorDetected;
  const errorDescription = loanCriteria?.errorDescription;
  const isDocumentationWithIncidence = loanCriteria?.loanApplicationSelected?.isDocumentationWithIncidence;
  const documentationDescription = loanCriteria?.loanApplicationSelected?.documentationDescription;
  const showCoborrowerUnderManagement = loan
    ? isCoborrowerUnderManagement(loan) && userEntity?.isRoleMerchantChannelOrAgent()
    : false;
  const errorOrIncidenceAlert = errorDetected || isDocumentationWithIncidence;

  let description = richStatus.DESCRIPTION_T_ID;

  if (showCoborrowerUnderManagement) {
    description = 'COBORROWER_UNDER_MANAGEMENT';
  }

  if (!richStatus) {
    return <>{status}</>;
  }

  const color = adjustColorToPalette(richStatus.colorPaletteName, richStatus.colorType, palette);

  return (
    <>
      <Tooltip
        enterDelay={300}
        title={
          <Box display={'flex'} flexDirection={'column'} gridGap={1}>
            <Box>{getTextIdTranslation(description)}</Box>
            {errorDescription && `(${getTextIdTranslation(errorDescription)})`}
          </Box>
        }
      >
        <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gridGap={12} minWidth={0} {...boxProps}>
          <Box bgcolor={color} className={'circle'} style={{ marginBottom: '-1px', maxHeight: 14 }} />
          <Box style={{ minWidth: 0 }}>
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={'center'}
              width={'100%'}
              overflow={'hidden'}
              textOverflow={'ellipsis'}
              whiteSpace={'nowrap'}
            >
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  lineHeight: 1,
                  maxWidth: errorOrIncidenceAlert ? 'calc(100% - 30px)' : '100%',
                }}
              >
                <Font variant="body2">
                  <Text tId={richStatus.SECTION_T_ID} />
                </Font>
                <Box className={'line-clamp-3'}>
                  {!hideSubText && (
                    <Font variant="caption" className="line-clamp-2" style={{ alignItems: 'center', gap: 4 }}>
                      <Text tId={description} />
                      {showCoborrowerUnderManagement && (
                        <Icon style={{ fontSize: 16, color: palette.text.secondary }}>watch_later</Icon>
                      )}
                    </Font>
                  )}
                  {errorOrIncidenceAlert && (
                    <Box maxWidth={'180px'} className="line-clamp-1" style={{ whiteSpace: 'nowrap', minWidth: 0 }}>
                      <ErrorAndIncidenceDescription
                        errorDetected={errorDetected}
                        errorDescription={errorDescription}
                        isDocumentationWithIncidence={isDocumentationWithIncidence}
                        documentationDescription={documentationDescription}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
              {errorOrIncidenceAlert && (
                <Box ml={1}>
                  <ErrorIcon
                    errorDetected={errorDetected}
                    isDocumentationWithIncidence={isDocumentationWithIncidence}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Tooltip>
    </>
  );
};
